import axios, { AxiosRequestConfig } from 'axios';
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/users/me/profile',
    login: '/auth/admin/signin-email',
  },
  referrals: {
    create: '/referrals',
    get: '/referrals',
    badges: '/referrals/badges',
    reject: (id: string) => `/referrals/${id}/reject`,
  },
  notifications: {
    get: '/notifications',
    read: (id: string) => `/notifications/read/${id}`,
  },
  settings: {
    createOrUpdate: '/system-configs/create-or-update',
    get: '/system-configs',
  },
  dayOffs: {
    get: '/day-off-configs',
    create: '/day-off-configs',
    createOrUpdate: '/day-off-configs/create-or-update',
    getById: (id: string) => `/day-off-configs/${id}`,
    update: (id: string) => `/day-off-configs/${id}`,
    delete: (id: string) => `/day-off-configs/${id}`,
  },
  users: {
    get: '/admin/users',
    getById: (id: string) => `/admin/users/${id}`,
    update: (id: string) => `/admin/users/${id}`,
    detail: (id: string) => `/admin/users/${id}`,
    approve: (id: string) => `/admin/users/approve-verify/${id}`,
    reject: (id: string) => `/admin/users/reject-verify/${id}`,
    search: '/users/search',
    addUser: '/admin/users/add-user',
  },
  transactions: {
    get: '/user-transactions',
    getById: (id: string) => `/user-transactions/${id}`,
    meById: (id: string) => `/user-transactions/me/${id}`,
    badges: '/user-transactions/badges',
    approveTopup: (id: string) => `/user-transactions/admin/approve-topup-credit/${id}`,
    rejectTopup: (id: string) => `/user-transactions/admin/reject-topup-credit/${id}`,
    approveWithdraw: (id: string) => `/user-transactions/admin/approve-withdraw-credit/${id}`,
    rejectWithdraw: (id: string) => `/user-transactions/admin/reject-withdraw-credit/${id}`,
    approveRedeem: (id: string) => `/user-transactions/admin/approve-redeem/${id}`,
    rejectRedeem: (id: string) => `/user-transactions/admin/reject-redeem/${id}`,
    creditLoan: '/user-transactions/admin/credit-loan',
    expiring: '/user-transactions/almost-expired-positions',
    updateNewAppointmentDate: (id: string) => `/user-transactions/me/${id}/update-appointment-date`,
    stockReport: '/user-transactions/admin/stock-report',

    // position
    position: {
      buy: '/user-transactions/me/buy-position',
      sell: '/user-transactions/me/sell-position',
      buyApprove: (id: string) => `/user-transactions/admin/approve-buy-position/${id}`,
      buyReject: (id: string) => `/user-transactions/admin/reject-buy-position/${id}`,
      sellApprove1: (id: string) => `/user-transactions/admin/approve-sell-position-1/${id}`,
      sellApprove2: (id: string) => `/user-transactions/admin/approve-sell-position-2/${id}`,
      sellReject: (id: string) => `/user-transactions/admin/reject-sell-position/${id}`,
      open: '/user-transactions/me/open-positions',
      waitingBuy: '/user-transactions/me/waiting-buy-position',
      waitingSell: '/user-transactions/me/waiting-sell-position',
      cancelWaiting: (id: string) => `/user-transactions/me/cancel-waiting-position/${id}`,
      cancelBuyPosition: (id: string) => `/user-transactions/me/cancel-buy-position/${id}`,
      paymentBuyPosition: (id: string) => `/user-transactions/me/payment-buy-position/${id}`,
    },

    redeem: {
      create: '/user-transactions/me/redeem',
      stock: '/user-transactions/admin/redeem-stock',
      approve: (id: string) => `/user-transactions/admin/approve-redeem/${id}`,
      reject: (id: string) => `/user-transactions/admin/reject-redeem/${id}`,
    },

    deposit: {
      create: '/user-transactions/me/deposit',
      stock: '/user-transactions/admin/deposit-stock',
      approve: (id: string) => `/user-transactions/admin/approve-deposit/${id}`,
      reject: (id: string) => `/user-transactions/admin/reject-deposit/${id}`,
    },

    topupDeposit: {
      create: '/user-transactions/me/topup-deposit',
      approve: (id: string) => `/user-transactions/admin/approve-topup-deposit/${id}`,
      reject: (id: string) => `/user-transactions/admin/reject-topup-deposit/${id}`,
    },

    topupCredit: {
      create: '/user-transactions/me/topup-credit',
    },

    withdrawCredit: {
      create: '/user-transactions/me/withdraw-credit',
    },
  },
  reports: {
    transactions: '/dashboard/transactions/report',
  },
  branch: {
    get: '/branches',
    create: '/branches',
    getById: (id: string) => `/branches/${id}`,
    update: (id: string) => `/branches/${id}`,
    delete: (id: string) => `/branches/${id}`,
  },
  adminBankAccount: {
    get: '/admin-bank-accounts',
    create: '/admin-bank-accounts',
    getById: (id: string) => `/admin-bank-accounts/${id}`,
    update: (id: string) => `/admin-bank-accounts/${id}`,
    delete: (id: string) => `/admin-bank-accounts/${id}`,
  },
  product: {
    get: '/products',
    getAllForUser: '/products/find-all-for-user',
    post: '/products',
    getById: (id: string) => `/products/${id}`,
    update: (id: string) => `/products/${id}`,
    delete: (id: string) => `/products/${id}`,
    priceLog: '/product-price-logs',
    updateSequence: (id: string) => `/products/${id}/update-sequence`,
  },
  productRedeemFees: {
    get: '/product-redeem-fees',
    post: '/product-redeem-fees',
    update: (id: string) => `/product-redeem-fees/${id}`,
    delete: (id: string) => `/product-redeem-fees/${id}`,
  },
  userLevel: {
    get: '/user-levels',
    update: (id: string) => `/user-levels/${id}`,
  },
  backAccount: {
    get: '/admin-bank-accounts',
    create: '/admin-bank-accounts',
    getById: (id: string) => `/admin-bank-accounts/${id}`,
    delete: (id: string) => `/admin-bank-accounts/${id}`,
    update: (id: string) => `/admin-bank-accounts/${id}`,
    setDefault: (id: string) => `/admin-bank-accounts/${id}/set-default`,
    bank: '/banks',
  },
  productPositionQuotas: {
    get: '/user-product-position-quotas',
    post: '/user-product-position-quotas',
    update: (id: string) => `/user-product-position-quotas/${id}`,
  },
  sendPdf: {
    get: (id: string) => `/user-transactions/admin/send-pdf-to-user/${id}`,
  },
  stocks: {
    get: '/stocks',
    getSummary: '/stocks/summary/',
    getSummaryByProductId: (id: string) => `/stocks/summary/${id}`,
    getMattProfit: '/stocks/matt-profit',
    report: '/stocks/report',
  },
  stockFactories: {
    get: '/stock-factories',
    create: '/stock-factories',
    getById: (id: string) => `/stock-factories/${id}`,
    update: (id: string) => `/stock-factories/${id}`,
    delete: (id: string) => `/stock-factories/${id}`,
    getSummaryByProductId: (id: string) => `/stock-factories/summary/${id}`,
  },
  userLevelProduct: {
    get: '/user-level-products',
    update: (id: string) => `/user-level-products/${id}`,
  },
  userLevelPrice: {
    get: '/user-level-prices',
    update: (id: string) => `/user-level-prices/${id}`,
  },
  productPriceLog: {
    getLatest: '/product-price-logs/get-latest-price-log',
  },
  productAmountConfig: {
    main: '/product-amount-configs',
    byId: (id: string) => `/product-amount-configs/${id}`,
  },
  printReport: {
    stockUsers: '/print-report/stock-users',
    transactions: '/print-report',
  },
};
